type BrowserStorageKind = 'localStorage' | 'sessionStorage'

class ResilientBrowserStorage implements Storage {
  private readonly fallback = new Map<string, string>()

  constructor(private readonly kind: BrowserStorageKind) {}

  private native_storage(): Storage | null {
    try {
      const candidate = (globalThis as unknown as Record<string, unknown>)[this.kind]
      return candidate && typeof candidate === 'object' ? candidate as Storage : null
    } catch {
      return null
    }
  }

  get length(): number {
    try {
      return this.native_storage()?.length ?? this.fallback.size
    } catch {
      return this.fallback.size
    }
  }

  clear(): void {
    this.fallback.clear()
    try {
      this.native_storage()?.clear()
    } catch {
      // Webviews e políticas de privacidade podem bloquear storage por completo.
    }
  }

  getItem(key: string): string | null {
    try {
      const native = this.native_storage()
      if (native) return native.getItem(key)
    } catch {
      // O fallback mantém a sessão funcional enquanto este documento existir.
    }
    return this.fallback.get(key) ?? null
  }

  key(index: number): string | null {
    try {
      const native = this.native_storage()
      if (native) return native.key(index)
    } catch {
      // Continua pelo fallback em memória.
    }
    return Array.from(this.fallback.keys())[index] ?? null
  }

  removeItem(key: string): void {
    this.fallback.delete(key)
    try {
      this.native_storage()?.removeItem(key)
    } catch {
      // Remover do fallback já evita reutilização no documento atual.
    }
  }

  setItem(key: string, value: string): void {
    const serialized = String(value)
    try {
      const native = this.native_storage()
      if (native) {
        native.setItem(key, serialized)
        this.fallback.delete(key)
        return
      }
    } catch {
      // Continua pelo fallback quando o storage do navegador é indisponível.
    }
    this.fallback.set(key, serialized)
  }
}

export const browser_local_storage: Storage = new ResilientBrowserStorage('localStorage')
export const browser_session_storage: Storage = new ResilientBrowserStorage('sessionStorage')

// Comparar a área do evento também pode lançar SecurityError. Centralize
// esse acesso junto das leituras/escritas protegidas do adaptador.
export function is_local_storage_event(event: StorageEvent): boolean {
  try {
    return !event.storageArea || event.storageArea === globalThis.localStorage
  } catch {
    return false
  }
}
